import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, CheckCircle, Globe, Clock, TrendingUp } from 'lucide-react';
import AnimatedElement from '../utils/AnimatedElement';

const AboutPreview = () => {
  const stats = [
    { label: 'Countries Served', value: '15+', icon: Globe },
    { label: 'Active Projects', value: '50+', icon: TrendingUp },
    { label: 'Support Coverage', value: '24/7', icon: Clock },
  ];

  const capabilities = [
    'Production ML systems processing 100M+ records daily',
    'Multi-cloud deployments across AWS, GCP, Azure',
    'IoT networks connecting 10,000+ devices globally',
  ];

  return (
    <section className="py-24 relative overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1">
            <AnimatedElement animation="slideRight" duration={0.6}>
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-subtle border border-gray-100 dark:border-gray-700 p-8">
                <h3 className="text-xl font-semibold mb-4">Our Approach</h3>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  We don't build demos. We build systems that run in production, handle real traffic, 
                  and solve actual business problems. Every solution is designed for scale, reliability, and maintainability.
                </p>
                
                <div className="grid grid-cols-3 gap-4 mt-8">
                  {stats.map((stat, index) => (
                    <div key={index} className="text-center">
                      <div className="flex justify-center mb-2">
                        <stat.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div className="text-2xl font-bold mb-1">{stat.value}</div>
                      <div className="text-xs text-muted-foreground">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedElement>
          </div>
          
          <div className="order-1 lg:order-2">
            <AnimatedElement animation="slideLeft" duration={0.6}>
              <span className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                About Us
              </span>
              <AnimatedElement animation="slideUp" delay={0.1}>
                <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-6">
                  Built for <span className="text-primary">Production</span>, Not Prototypes
                </h2>
              </AnimatedElement>
              <AnimatedElement animation="slideUp" delay={0.2}>
                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Founded in 2025 by engineers who've shipped production AI systems at scale. 
                  We work with companies across manufacturing, retail, finance, and logistics to build 
                  systems that actually work in the real world.
                </p>
              </AnimatedElement>
              
              <div className="space-y-3 mb-8">
                {capabilities.map((capability, index) => (
                  <AnimatedElement key={index} animation="slideLeft" delay={0.3 + index * 0.1}>
                    <div className="flex items-start">
                      <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5 mr-3" />
                      <p className="text-muted-foreground">{capability}</p>
                    </div>
                  </AnimatedElement>
                ))}
              </div>
              
              <AnimatedElement animation="slideUp" delay={0.6}>
                <Link
                  to="/about"
                  onClick={() => window.scrollTo(0, 0)}
                  className="glass-button-primary px-6 py-3 text-base font-medium inline-block group"
                >
                  Learn More About Us
                  <ArrowUpRight className="ml-2 h-4 w-4 inline-block transform transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </Link>
              </AnimatedElement>
            </AnimatedElement>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutPreview;
